import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import defaultsDeep from 'lodash/defaultsDeep';
import kebabCase from 'lodash/kebabCase';

import utils from './utils';
import { BottomIcon, TopIcon } from '../Icons/Icons';

const defaultProps = {
    column: {
        align: 'left',
        className: '',
        sort: {
            default: false,
        },
    },
};

class DataTableHeading extends React.Component {
    constructor(props) {
        super(props);

        this.handleSort = this.handleSort.bind(this);
    }
    state = {
        showModal: false,
    }

    getSortDirection() {
        const { sortedBy } = this.props;
        const direction = sortedBy && sortedBy.length > 0 ?
            sortedBy[0].direction
            :
            '';

        return typeof direction === 'string' && direction === 'DESC' ? 'DESC' : 'ASC';
    }

    getAriaSort() {
        let ariaSort = 'none';
        const isSorted = this.isSorted();

        if (isSorted) {
            const sortDirection = this.getSortDirection();
            ariaSort = sortDirection === 'ASC' ? 'ascending' : 'descending';
        }

        return ariaSort;
    }

    isSortable() {
        const { column, sortable } = this.props;

        return utils.getFirstDefined(column.sortable, sortable, false);
    }

    isSorted() {
        const { column: { accessor }, sortedBy } = this.props;
        const selector = sortedBy && sortedBy.length > 0 ? sortedBy[0].selector : null;
        return utils.normalizeAccessor(accessor) === selector;
    }

    handleSort(e) {
        e.preventDefault();
        const { name } = e.target;
        const { column: { accessor, sort, profileHeader }, onSortChange, sortedBy } = this.props;
        const previousSort = sortedBy && sortedBy.length ? sortedBy[0].selector : null;
        let sortDirection = this.getSortDirection() === 'ASC' ? 'DESC' : 'ASC';
        if (previousSort && previousSort !== accessor) {
            sortDirection = 'ASC';
        }
        const updatedSort = [
            {
                selector: utils.normalizeAccessor(accessor),
                direction: sortDirection,
            },
        ];

        if (sort && sort.secondary) {
            updatedSort.push({
                selector: utils.normalizeAccessor(sort.secondary),
                direction: 'ASC',
            });
        }
        if (!(profileHeader && name !== 'sort-profile')) {
            onSortChange(updatedSort);
        }
    }

    toggleCompetencies = (e) => {
        const props = defaultsDeep({}, this.props, defaultProps);
        const { column: { setShowCompetencies } } = props;
        e.preventDefault();
        if (setShowCompetencies) setShowCompetencies();
    }

    renderSortIcon(showProfileIcon) {
        const isSorted = this.isSorted();
        const sortDirection = this.getSortDirection();
        const props = defaultsDeep({}, this.props, defaultProps);
        const { column: { profileHeader } } = props;

        if ((profileHeader && !showProfileIcon)) {
            return (<span className="data-table__sort-icon"><BottomIcon height={5} width={9} /></span>);
        }

        if (!isSorted) {
            return null;
        }
        return (
            <span className="data-table__sort-icon">
                {
                    sortDirection === 'ASC' ?
                        <TopIcon height={5} width={9} />
                        :
                        <BottomIcon height={5} width={9} />
                }
            </span>);
    }

    render() {
        const props = defaultsDeep({}, this.props, defaultProps);
        const {
            column: {
                accessor,
                align,
                className,
                title,
                type,
                profileHeader,
            },
        } = props;
        const selector = kebabCase(utils.normalizeAccessor(accessor));
        const isSortable = this.isSortable();
        const isSorted = this.isSorted();
        const headingClassName = classNames('data-table__heading', {
            [`data-table__heading--${selector}`]: true,
            [`data-table__heading--${align}`]: true,
            'data-table__heading--sortable': isSortable,
            'data-table__heading--sorted': isSorted,
        }, className);
        const ariaSort = this.getAriaSort();

        if (type === 'descriptive') {
            return (
                <th aria-sort={ariaSort} className={headingClassName}>
                    {
                        isSortable ?
                            <button
                                className="data-table__heading-control"
                                onClick={this.handleSort}
                                type="button"
                            >
                                <span className="data-table__sort">
                                    <span style={{ color: '#7f7f7f' }}>
                                        {title}
                                    </span>
                                    {this.renderSortIcon()}
                                </span>
                            </button>
                            :
                            <span style={{ color: '#7f7f7f' }}>
                                {title}
                            </span>}
                </th>
            );
        }

        const offset = (el) => {
            const rect = el.getBoundingClientRect();
            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
        };

        const element = document.getElementById(accessor);
        const elementOffset = element && offset(element);

        return (
            <th aria-sort={ariaSort} className={headingClassName} >
                {
                    isSortable ?
                        <button
                            className="data-table__heading-control"
                            onClick={(e) => {
                                this.handleSort(e);
                                this.setState({ showModal: true });
                            }}
                            type="button"
                            id={accessor}
                        >
                            <span className="data-table__sort">
                                <span>{title}</span>
                                {this.renderSortIcon()}
                            </span>
                        </button>
                        :
                        title
                }
                {this.state.showModal && type === 'scores' && profileHeader &&
                    (
                        <React.Fragment>
                            <div className="data-table__modal" style={{ left: elementOffset.left - 300 }}>
                                <button name="sort-profile" className="sort-profiles-sort-button" appearance="secondary" onClick={this.handleSort}>
                                    Sort
                                    {this.renderSortIcon(true)}
                                </button>
                                <button appearance="secondary" onClick={this.toggleCompetencies}>View Competencies</button>
                            </div>
                            <div
                                className="data-table__clear-modal"
                                onClick={(e) => {
                                    e.preventDefault();
                                    this.setState({ showModal: false });
                                }}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                    this.setState({ showModal: false });
                                }}
                                tabIndex={0}
                                role="button"
                            />
                        </React.Fragment>
                    )}
            </th>
        );
    }
}

DataTableHeading.propTypes = {
    column: PropTypes.shape({
        accessor: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.string,
        ]).isRequired,
        align: PropTypes.oneOf([
            'center',
            'left',
            'right',
        ]),
        className: PropTypes.string,
        sortable: PropTypes.bool,
        sort: PropTypes.shape({
            default: PropTypes.oneOfType([
                PropTypes.bool,
                PropTypes.oneOf(['ASC', 'DESC']),
            ]),
            enabled: PropTypes.bool,
            secondary: PropTypes.oneOfType([
                PropTypes.func,
                PropTypes.string,
            ]),
        }),
        title: PropTypes.string.isRequired,
        type: PropTypes.string,
    }),
    onSortChange: PropTypes.func.isRequired,
    sortable: PropTypes.bool.isRequired,
    sortedBy: PropTypes.arrayOf(PropTypes.shape({
        selector: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.string,
        ]),
    })).isRequired,
};

DataTableHeading.defaultProps = defaultProps;

export default DataTableHeading;
